import React, { useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// Language options. Values match the i18n resource keys defined in src/i18n.js.
// Each language is labelled in its own script (endonym) so a visitor can find
// their language without already reading the current one.
// The first eight are the EU MDR submission languages, listed first and in the
// order they appear in the submission; the rest are the pre-existing locales.
const LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'el', label: 'Ελληνικά' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'hu', label: 'Magyar' },
  { code: 'it', label: 'Italiano' },
  { code: 'pl', label: 'Polski' },
  { code: 'ko', label: '한국어' },
  { code: 'jp', label: '日本語' },
  { code: 'ru', label: 'Русский' },
  { code: 'zhchs', label: '简体中文' },
  { code: 'zhcht', label: '繁體中文' },
];

// Visitor-facing top nav. Both the country picker and the language switcher are
// rendered as the same minimal link-style dropdown for visual consistency.
// The country picker (shown only when `countryOptions` is provided) is a visitor
// setting that drives which manuals the portal shows; the admin console renders
// this header without it.
const EifuHeader = ({ country, onCountryChange, countryOptions }) => {
  const { t, i18n } = useTranslation(['translation']);
  const fallbackLang = LANGS.find((x) => x.code === 'en');
  const current = LANGS.find((x) => x.code === i18n.language) || fallbackLang;
  const [q, setQ] = useState('');

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
  };

  const showCountry = Array.isArray(countryOptions) && countryOptions.length > 0;
  const selectedCountryLabel = useMemo(
    () => countryOptions?.find((x) => x.value === country)?.label,
    [countryOptions, country]
  );
  const filteredCountries = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) {
      return countryOptions || [];
    }
    return (countryOptions || []).filter((o) => o.label.toLowerCase().includes(needle));
  }, [countryOptions, q]);

  return (
    <nav className="navbar navbar-expand-lg fixed-top navbar-bg-light">
      <div className="container flex-lg-row flex-nowrap align-items-center">
        <div className="navbar-brand">
          <a href="/" className="d-flex align-items-center text-decoration-none">
            {/* Simple text wordmark — no third-party product logo. */}
            <span className="fw-bold" style={{ fontSize: '1.3rem', letterSpacing: '0.02em' }}>
              Ray <span className="text-uppercase">eIFU</span>
            </span>
          </a>
        </div>
        <div className="navbar-other ms-auto d-flex align-items-center gap-3">
          {showCountry && (
            <Dropdown align="end" onToggle={(open) => open && setQ('')}>
              <Dropdown.Toggle
                variant="link"
                id="eifu-country"
                className="text-decoration-none fw-medium"
              >
                <i className="uil uil-location-point me-1" />
                {selectedCountryLabel || t('Country')}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxHeight: '360px', overflowY: 'auto', minWidth: '230px' }}>
                <div className="px-2 pb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t('Search')}
                    value={q}
                    autoFocus
                    onChange={(e) => setQ(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <Dropdown.Item active={!country} onClick={() => onCountryChange && onCountryChange('')}>
                  {t('All countries')}
                </Dropdown.Item>
                {filteredCountries.map((o) => (
                  <Dropdown.Item
                    key={o.value}
                    active={o.value === country}
                    onClick={() => onCountryChange && onCountryChange(o.value)}
                  >
                    {o.label}
                  </Dropdown.Item>
                ))}
                {filteredCountries.length === 0 && (
                  <div className="px-3 py-2 text-muted small">{t('No results')}</div>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="link"
              id="eifu-lang"
              className="text-decoration-none fw-medium"
            >
              <i className="uil uil-globe me-1" />
              {current.label}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {LANGS.map((x) => (
                <Dropdown.Item
                  key={x.code}
                  active={x.code === current.code}
                  onClick={() => changeLang(x.code)}
                >
                  {x.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </nav>
  );
};

export default EifuHeader;
